/**
 * Utility nodes — timers, delays, random, interpolation.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// TIMERS
// ============================================================================

export const Delay: NodeBehaviour = {
  id: 'utility.delay',
  name: 'Delay',
  category: 'Utility',
  color: '#3498db',
  icon: 'Clock',
  description: 'Delays execution for a specified time.',
  keywords: ['wait', 'timer', 'sleep'],
  inputs: [
    flowIn(),
    input('duration', 'Duration (s)', 'f32', 1.0),
  ],
  outputs: [
    flowOut('completed', 'Completed'),
  ],
  compile(ctx) {
    ctx.emit('bridge:delay', [ctx.resolveInput('duration')], 'void', true);
  },
  execute(ctx) {
    // In interpreter mode, delay cannot await — pass through immediately
    ctx.triggerFlow('completed');
  },
};

export const Timer: NodeBehaviour = {
  id: 'utility.timer',
  name: 'Timer',
  category: 'Utility',
  color: '#3498db',
  icon: 'Timer',
  description: 'Starts a repeating timer that fires at intervals.',
  keywords: ['interval', 'repeat', 'tick'],
  inputs: [
    flowIn('start', 'Start'),
    flowIn('stop', 'Stop'),
    input('interval', 'Interval (s)', 'f32', 1.0),
    input('repeat', 'Repeat', 'bool', true),
  ],
  outputs: [
    flowOut('tick', 'On Tick'),
    output('count', 'Tick Count', 'i32'),
  ],
  compile(ctx) {
    ctx.emit('bridge:timer', [ctx.resolveInput('interval'), ctx.resolveInput('repeat')], 'void', true);
  },
  execute(ctx) {
    // In interpreter mode, fire tick once per invocation
    const count = (ctx.getState<number>('__timerCount') ?? 0) + 1;
    ctx.setState('__timerCount', count);
    ctx.setOutput('count', count);
    ctx.triggerFlow('tick');
  },
};

export const GetTime: NodeBehaviour = {
  id: 'utility.getTime',
  name: 'Get Time',
  category: 'Utility',
  isPure: true,
  color: '#3498db',
  icon: 'Clock',
  description: 'Gets the current game time.',
  keywords: ['elapsed', 'seconds'],
  inputs: [],
  outputs: [
    output('time', 'Time', 'f32'),
    output('unscaled', 'Unscaled Time', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getTime', [], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('time', ctx.time);
    ctx.setOutput('unscaled', ctx.time);
  },
};

export const GetDeltaTime: NodeBehaviour = {
  id: 'utility.getDeltaTime',
  name: 'Get Delta Time',
  category: 'Utility',
  isPure: true,
  color: '#3498db',
  icon: 'Clock',
  description: 'Gets the time since last frame.',
  keywords: ['dt', 'frame'],
  inputs: [],
  outputs: [
    output('dt', 'Delta Time', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('event_param:deltaTime', [], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('dt', ctx.deltaTime);
  },
};

export const GetFrameCount: NodeBehaviour = {
  id: 'utility.getFrameCount',
  name: 'Get Frame Count',
  category: 'Utility',
  isPure: true,
  color: '#3498db',
  icon: 'Hash',
  description: 'Gets the total number of frames since start.',
  keywords: ['frame', 'count'],
  inputs: [],
  outputs: [
    output('frame', 'Frame', 'i32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getFrameCount', [], 'i32');
  },
  execute(ctx) {
    ctx.setOutput('frame', ctx.bridge.call('bridge:getFrameCount') as number ?? 0);
  },
};

// ============================================================================
// RANDOM
// ============================================================================

export const RandomFloat: NodeBehaviour = {
  id: 'utility.randomFloat',
  name: 'Random Float',
  category: 'Utility',
  isPure: true,
  color: '#9b59b6',
  icon: 'Shuffle',
  description: 'Returns a random float between min and max.',
  keywords: ['rand', 'number'],
  inputs: [
    input('min', 'Min', 'f32', 0),
    input('max', 'Max', 'f32', 1),
  ],
  outputs: [
    output('value', 'Value', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:randomFloat', [ctx.resolveInput('min'), ctx.resolveInput('max')], 'f32');
  },
  execute(ctx) {
    const min = ctx.getInput<number>('min');
    const max = ctx.getInput<number>('max');
    ctx.setOutput('value', Math.random() * (max - min) + min);
  },
};

export const RandomInt: NodeBehaviour = {
  id: 'utility.randomInt',
  name: 'Random Int',
  category: 'Utility',
  isPure: true,
  color: '#9b59b6',
  icon: 'Shuffle',
  description: 'Returns a random integer between min and max (inclusive).',
  keywords: ['rand', 'number', 'integer'],
  inputs: [
    input('min', 'Min', 'i32', 0),
    input('max', 'Max', 'i32', 100),
  ],
  outputs: [
    output('value', 'Value', 'i32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:randomInt', [ctx.resolveInput('min'), ctx.resolveInput('max')], 'i32');
  },
  execute(ctx) {
    const min = ctx.getInput<number>('min');
    const max = ctx.getInput<number>('max');
    ctx.setOutput('value', Math.floor(Math.random() * (max - min + 1)) + min);
  },
};

export const RandomBool: NodeBehaviour = {
  id: 'utility.randomBool',
  name: 'Random Bool',
  category: 'Utility',
  isPure: true,
  color: '#9b59b6',
  icon: 'Shuffle',
  description: 'Returns true with the given probability (0-1).',
  keywords: ['rand', 'chance', 'coin'],
  inputs: [
    input('probability', 'Probability', 'f32', 0.5),
  ],
  outputs: [
    output('value', 'Value', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:randomBool', [ctx.resolveInput('probability')], 'bool');
  },
  execute(ctx) {
    ctx.setOutput('value', Math.random() < ctx.getInput<number>('probability'));
  },
};

export const RandomDirection: NodeBehaviour = {
  id: 'utility.randomDirection',
  name: 'Random Direction',
  category: 'Utility',
  isPure: true,
  color: '#9b59b6',
  icon: 'Shuffle',
  description: 'Returns a random 2D unit vector (direction).',
  keywords: ['rand', 'vector', 'angle'],
  inputs: [],
  outputs: [
    output('dir', 'Direction', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
    output('angle', 'Angle (rad)', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:randomDirection', [], 'vec2');
  },
  execute(ctx) {
    const angle = Math.random() * Math.PI * 2;
    const x = Math.cos(angle);
    const y = Math.sin(angle);
    ctx.setOutput('dir', { x, y });
    ctx.setOutput('x', x);
    ctx.setOutput('y', y);
    ctx.setOutput('angle', angle);
  },
};

export const RandomPointInCircle: NodeBehaviour = {
  id: 'utility.randomPointInCircle',
  name: 'Random Point in Circle',
  category: 'Utility',
  isPure: true,
  color: '#9b59b6',
  icon: 'Circle',
  description: 'Returns a random point inside a circle.',
  keywords: ['rand', 'position', 'area'],
  inputs: [
    input('centerX', 'Center X', 'f32', 0),
    input('centerY', 'Center Y', 'f32', 0),
    input('radius', 'Radius', 'f32', 1),
  ],
  outputs: [
    output('point', 'Point', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:randomPointInCircle', [
      ctx.resolveInput('centerX'),
      ctx.resolveInput('centerY'),
      ctx.resolveInput('radius'),
    ], 'vec2');
  },
  execute(ctx) {
    const cx = ctx.getInput<number>('centerX');
    const cy = ctx.getInput<number>('centerY');
    const r = ctx.getInput<number>('radius') * Math.sqrt(Math.random());
    const a = Math.random() * Math.PI * 2;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    ctx.setOutput('point', { x, y });
    ctx.setOutput('x', x);
    ctx.setOutput('y', y);
  },
};

export const RandomPointInRect: NodeBehaviour = {
  id: 'utility.randomPointInRect',
  name: 'Random Point in Rectangle',
  category: 'Utility',
  isPure: true,
  color: '#9b59b6',
  icon: 'Square',
  description: 'Returns a random point inside a rectangle.',
  keywords: ['rand', 'position', 'area', 'box'],
  inputs: [
    input('x', 'X', 'f32', 0),
    input('y', 'Y', 'f32', 0),
    input('width', 'Width', 'f32', 1),
    input('height', 'Height', 'f32', 1),
  ],
  outputs: [
    output('point', 'Point', 'vec2'),
    output('px', 'X', 'f32'),
    output('py', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:randomPointInRect', [
      ctx.resolveInput('x'),
      ctx.resolveInput('y'),
      ctx.resolveInput('width'),
      ctx.resolveInput('height'),
    ], 'vec2');
  },
  execute(ctx) {
    const rx = ctx.getInput<number>('x');
    const ry = ctx.getInput<number>('y');
    const w = ctx.getInput<number>('width');
    const h = ctx.getInput<number>('height');
    const px = rx + Math.random() * w;
    const py = ry + Math.random() * h;
    ctx.setOutput('point', { x: px, y: py });
    ctx.setOutput('px', px);
    ctx.setOutput('py', py);
  },
};

// ============================================================================
// INTERPOLATION
// ============================================================================

export const Lerp: NodeBehaviour = {
  id: 'utility.lerp',
  name: 'Lerp',
  category: 'Utility',
  isPure: true,
  color: '#1abc9c',
  icon: 'TrendingUp',
  description: 'Linearly interpolates between two values.',
  keywords: ['blend', 'mix', 'interpolate'],
  inputs: [
    input('a', 'A', 'f32', 0),
    input('b', 'B', 'f32', 1),
    input('t', 'T', 'f32', 0.5),
  ],
  outputs: [
    output('result', 'Result', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:lerp', [ctx.resolveInput('a'), ctx.resolveInput('b'), ctx.resolveInput('t')], 'f32');
  },
  execute(ctx) {
    const a = ctx.getInput<number>('a');
    const b = ctx.getInput<number>('b');
    const t = ctx.getInput<number>('t');
    ctx.setOutput('result', a + (b - a) * t);
  },
};

export const LerpVec2: NodeBehaviour = {
  id: 'utility.lerpVec2',
  name: 'Lerp Vec2',
  category: 'Utility',
  isPure: true,
  color: '#1abc9c',
  icon: 'TrendingUp',
  description: 'Linearly interpolates between two 2D vectors.',
  keywords: ['blend', 'mix', 'interpolate', 'vector'],
  inputs: [
    input('ax', 'A X', 'f32', 0),
    input('ay', 'A Y', 'f32', 0),
    input('bx', 'B X', 'f32', 1),
    input('by', 'B Y', 'f32', 1),
    input('t', 'T', 'f32', 0.5),
  ],
  outputs: [
    output('result', 'Result', 'vec2'),
    output('x', 'X', 'f32'),
    output('y', 'Y', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:lerpVec2', [
      ctx.resolveInput('ax'), ctx.resolveInput('ay'),
      ctx.resolveInput('bx'), ctx.resolveInput('by'),
      ctx.resolveInput('t'),
    ], 'vec2');
  },
  execute(ctx) {
    const ax = ctx.getInput<number>('ax');
    const ay = ctx.getInput<number>('ay');
    const bx = ctx.getInput<number>('bx');
    const by = ctx.getInput<number>('by');
    const t = ctx.getInput<number>('t');
    const x = ax + (bx - ax) * t;
    const y = ay + (by - ay) * t;
    ctx.setOutput('result', { x, y });
    ctx.setOutput('x', x);
    ctx.setOutput('y', y);
  },
};

export const SmoothStep: NodeBehaviour = {
  id: 'utility.smoothstep',
  name: 'Smooth Step',
  category: 'Utility',
  isPure: true,
  color: '#1abc9c',
  icon: 'TrendingUp',
  description: 'Smooth Hermite interpolation between 0 and 1.',
  keywords: ['ease', 'smooth', 'curve'],
  inputs: [
    input('edge0', 'Edge 0', 'f32', 0),
    input('edge1', 'Edge 1', 'f32', 1),
    input('x', 'X', 'f32', 0.5),
  ],
  outputs: [
    output('result', 'Result', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:smoothstep', [ctx.resolveInput('edge0'), ctx.resolveInput('edge1'), ctx.resolveInput('x')], 'f32');
  },
  execute(ctx) {
    const e0 = ctx.getInput<number>('edge0');
    const e1 = ctx.getInput<number>('edge1');
    const x = ctx.getInput<number>('x');
    let t = (x - e0) / (e1 - e0);
    t = Math.max(0, Math.min(1, t));
    ctx.setOutput('result', t * t * (3 - 2 * t));
  },
};

export const MoveTowards: NodeBehaviour = {
  id: 'utility.moveTowards',
  name: 'Move Towards',
  category: 'Utility',
  isPure: true,
  color: '#1abc9c',
  icon: 'ArrowRight',
  description: 'Moves a value towards a target by a maximum delta.',
  keywords: ['approach', 'step'],
  inputs: [
    input('current', 'Current', 'f32', 0),
    input('target', 'Target', 'f32', 1),
    input('maxDelta', 'Max Delta', 'f32', 1),
  ],
  outputs: [
    output('result', 'Result', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:moveTowards', [ctx.resolveInput('current'), ctx.resolveInput('target'), ctx.resolveInput('maxDelta')], 'f32');
  },
  execute(ctx) {
    const current = ctx.getInput<number>('current');
    const target = ctx.getInput<number>('target');
    const maxDelta = ctx.getInput<number>('maxDelta');
    const diff = target - current;
    ctx.setOutput('result', Math.abs(diff) <= maxDelta ? target : current + Math.sign(diff) * maxDelta);
  },
};

// ============================================================================
// CONVERSION
// ============================================================================

export const DegreesToRadians: NodeBehaviour = {
  id: 'utility.degToRad',
  name: 'Degrees to Radians',
  category: 'Utility',
  isPure: true,
  color: '#95a5a6',
  icon: 'RotateCw',
  description: 'Converts degrees to radians.',
  keywords: ['angle', 'convert'],
  inputs: [
    input('degrees', 'Degrees', 'f32', 0),
  ],
  outputs: [
    output('radians', 'Radians', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:degToRad', [ctx.resolveInput('degrees')], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('radians', ctx.getInput<number>('degrees') * Math.PI / 180);
  },
};

export const RadiansToDegrees: NodeBehaviour = {
  id: 'utility.radToDeg',
  name: 'Radians to Degrees',
  category: 'Utility',
  isPure: true,
  color: '#95a5a6',
  icon: 'RotateCw',
  description: 'Converts radians to degrees.',
  keywords: ['angle', 'convert'],
  inputs: [
    input('radians', 'Radians', 'f32', 0),
  ],
  outputs: [
    output('degrees', 'Degrees', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:radToDeg', [ctx.resolveInput('radians')], 'f32');
  },
  execute(ctx) {
    ctx.setOutput('degrees', ctx.getInput<number>('radians') * 180 / Math.PI);
  },
};

// ============================================================================
// EXPORT
// ============================================================================

export const UTILITY_NODES: NodeBehaviour[] = [
  // Timers
  Delay,
  Timer,
  GetTime,
  GetDeltaTime,
  GetFrameCount,
  // Random
  RandomFloat,
  RandomInt,
  RandomBool,
  RandomDirection,
  RandomPointInCircle,
  RandomPointInRect,
  // Interpolation
  Lerp,
  LerpVec2,
  SmoothStep,
  MoveTowards,
  // Conversion
  DegreesToRadians,
  RadiansToDegrees,
];
